import { Breadcrumbs, CrumbProps } from "@amzn/stencil-react-components/breadcrumbs";
import { Chip, ChipBackgroundColor } from "@amzn/stencil-react-components/chip";
import { Col, GridContainer, GridItem, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Status, StatusIndicator } from "@amzn/stencil-react-components/status-indicator";
import { H2, H3, Text } from "@amzn/stencil-react-components/text";
import IconCheckSmall from "@amzn/stencil-react-icons/icons/icon-check-small";
import IconExternalLinkSmall from "@amzn/stencil-react-icons/icons/icon-external-link-small";
import Button, { ButtonVariant } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { Link } from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { trackUxmCustomEvent } from "common/helpers/analytics-helper";
import { createItemTaxonomy } from "common/helpers/taxonomy-helper";
import { Paths } from "common/paths/paths";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";

import { RelatedUseCases } from "./components/RelatedUseCases";
import { UseCaseDiscussion } from "./components/UseCaseDiscussion";
import { UseCaseImpactPanel } from "./components/UseCaseImpactPanel";
import { UseCaseMetaBar } from "./components/UseCaseMetaBar";
import { UseCaseNotFound } from "./components/UseCaseNotFound";
import { UseCaseSteps } from "./components/UseCaseSteps";
import { useRelatedUseCases } from "./hooks/useRelatedUseCases";
import { useToolNames } from "./hooks/useToolNames";
import { useUseCaseComments } from "./hooks/useUseCaseComments";
import { useUseCaseDetail } from "./hooks/useUseCaseDetail";
import { useUseCaseMetadata } from "./hooks/useUseCaseMetadata";
import { useUseCaseUsed } from "./hooks/useUseCaseUsed";

const UseCaseDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { useCase: d, isLoading } = useUseCaseDetail(id);
  const toolNames = useToolNames(d?.associatedToolIds ?? []);
  const { labelMap } = useUseCaseMetadata();
  const taxonomyId = id ? createItemTaxonomy(`atn::${id}`)?.toString() ?? `atn::${id}` : "";
  const { comments, postComment } = useUseCaseComments(taxonomyId);
  const { hasUsed, markUsed } = useUseCaseUsed(taxonomyId);
  const { related } = useRelatedUseCases(d);
  const totalCommentCount = comments.reduce((sum, c) => sum + 1 + (c.replies?.length ?? 0), 0);

  // Track page view
  useEffect(() => {
    trackUxmCustomEvent("usecase-detail-view", { useCaseId: id });
  }, [id]);

  if (isLoading) {
    return (
      <PageMainContainer>
        <Spinner size={SpinnerSize.Medium} />
      </PageMainContainer>
    );
  }

  if (!d) {
    return <UseCaseNotFound />;
  }

  return (
    <PageMainContainer>
      <Breadcrumbs
        crumbs={[
          { title: getString("logoText"), url: "/" },
          { title: getString("aiForMe.title"), url: "/aiforme" },
          { title: getString("useCases.breadcrumbs.useCases"), url: Paths.aiForMeUseCases.url },
          { title: d.name },
        ]}
        onCrumbClick={(e, crumb: CrumbProps) => {
          if (crumb.url) {
            e.preventDefault();
            history.push(crumb.url);
          }
        }}
      />
      <Col padding={{ top: "dimensions.spacing.400", bottom: "dimensions.spacing.600" }}>
        <GridContainer>
          <GridItem lg={12} md={8} sm={4}>
            <Col gridGap="dimensions.spacing.400" padding={{ top: "dimensions.spacing.300" }}>
              {/* Category + difficulty */}
              <Row gridGap="dimensions.spacing.200" alignItems="center">
                {d.categories?.map((cat) => (
                  <Chip key={cat} backgroundColor={ChipBackgroundColor.Purple}>
                    {labelMap[cat] || cat}
                  </Chip>
                ))}
                {d.difficultyLevel && (
                  <Chip backgroundColor={ChipBackgroundColor.Default}>
                    {labelMap[d.difficultyLevel] || d.difficultyLevel}
                  </Chip>
                )}
              </Row>

              {/* Title + actions */}
              <H2>{d.name}</H2>
              <Row gridGap="dimensions.spacing.200">
                <Button
                  variant={hasUsed ? ButtonVariant.Primary : ButtonVariant.Secondary}
                  touchpoint="usecase-i-used-this"
                  customData={{ useCaseId: id }}
                  disabled={hasUsed}
                  onClick={markUsed}
                >
                  {hasUsed && <IconCheckSmall aria-hidden="true" />}
                  {hasUsed ? getString("useCases.detail.iUsedThisActive") : getString("useCases.detail.iUsedThis")}
                </Button>
                <Button
                  variant={ButtonVariant.Primary}
                  touchpoint="usecase-contribute-cta"
                  customData={{ useCaseId: id }}
                  onClick={() => history.push(Paths.aiForMeUseCaseContribute.url)}
                >
                  {getString("useCases.detail.contribute")}
                </Button>
                <Button
                  variant={ButtonVariant.Secondary}
                  touchpoint="usecase-back-to-all"
                  customData={{ useCaseId: id }}
                  onClick={() => history.push(Paths.aiForMeUseCases.url)}
                >
                  {getString("useCases.detail.backToAll")}
                </Button>
              </Row>
              <Text fontSize="T200">{d.shortDescription}</Text>

              {/* Contributor + tools + audience */}
              <UseCaseMetaBar
                contributorAlias={d.contributorAlias}
                date={
                  d.publishedAt
                    ? new Date(d.publishedAt).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : ""
                }
                commentCount={totalCommentCount}
                usedCount={d.usedCount ?? 0}
                tools={(d.associatedToolIds ?? []).map((id) => toolNames[id] || id)}
                recommendedAudience={(d.recommendedAudience ?? []).map((a) => labelMap[a] || a)}
              />

              {/* Impact */}
              {d.impact && (
                <>
                  <H3>{getString("useCases.detail.impact")}</H3>
                  <UseCaseImpactPanel impact={d.impact} />
                </>
              )}

              {/* Steps */}
              {d.steps?.length > 0 && (
                <>
                  <H3>{getString("useCases.detail.steps")}</H3>
                  <UseCaseSteps steps={d.steps} useCaseId={id} />
                </>
              )}

              {/* Prerequisites */}
              {d.prerequisites && d.prerequisites.length > 0 && (
                <>
                  <Spacer height="dimensions.spacing.200" />
                  <H3>{getString("useCases.detail.prerequisites")}</H3>
                  <Col gridGap="dimensions.spacing.200">
                    {d.prerequisites.map((p, i) => (
                      <StatusIndicator key={i} status={Status.Positive} messageText={p} />
                    ))}
                  </Col>
                </>
              )}

              {/* Learn more */}
              {d.learnMoreLinks && d.learnMoreLinks.length > 0 && (
                <>
                  <Spacer height="dimensions.spacing.200" />
                  <H3>{getString("useCases.detail.learnMore")}</H3>
                  <Col gridGap="dimensions.spacing.200">
                    {d.learnMoreLinks.map((link, i) => (
                      <Row key={i} gridGap="dimensions.spacing.100" alignItems="center">
                        <Link
                          href={link.url}
                          target="_blank"
                          touchpoint="usecase-learn-more"
                          customData={{ linkTitle: link.title }}
                        >
                          {link.title}
                        </Link>
                        <IconExternalLinkSmall aria-hidden="true" />
                      </Row>
                    ))}
                  </Col>
                </>
              )}

              {/* Discussion */}
              <Spacer height="dimensions.spacing.200" />
              <H3>{getString("useCases.detail.discussion", { count: String(totalCommentCount) })}</H3>
              <UseCaseDiscussion
                comments={comments.map((c) => ({
                  id: c.commentId,
                  authorAlias: c.authorAlias,
                  authorName: c.authorFullName,
                  date: c.createdAt,
                  text: c.text,
                  replies: c.replies?.map((r) => ({
                    id: r.commentId,
                    authorAlias: r.authorAlias,
                    authorName: r.authorFullName,
                    date: r.createdAt,
                    text: r.text,
                  })),
                }))}
                useCaseId={id}
                onPostComment={(text) => postComment(text)}
                onPostReply={(text, parentId) => postComment(text, parentId)}
              />

              {/* Related use cases */}
              <RelatedUseCases useCases={related} />
            </Col>
          </GridItem>
        </GridContainer>
      </Col>
    </PageMainContainer>
  );
};

export default UseCaseDetailPage;
